import { gql, useMutation, useQuery } from '@apollo/client'
import flatten from 'lodash/flatten'
import pick from 'lodash/pick'
import set from 'lodash/set'
import { useMemo } from 'react'

import {
  ArtistSubscriptionPlan,
  ArtistSubscriptionPlanQuery,
  ArtistSubscriptionPlanQueryVariables,
  CreateAcceptedItemMutation,
  CreateAcceptedItemMutationVariables,
  CreateItemInput,
  CreateResource,
  CreateResourceMutation,
  CreateResourceMutationVariables,
  ItemSelectBoxFragmentDoc,
  ItemType,
  Maybe,
  MembershipInterval,
  PersonalInformationType,
  SalesStatus,
  SkuSettingsInput,
  TicketItemMetadataThroughTicketInput,
} from '@/clients/utoniq-core/schema'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getResource } from '@/lib/resourceUpload'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'
import { personalInformationRequirementsValue } from './template/components'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleItemCreate')
  const { user } = useCurrentUser()

  const { showError } = useMessage()
  const { router, routerPages } = useRouter<{ resourceType: ItemType }>()

  const { upload, progress } = useUserTmpUploader(user?.uid || '')

  const { data } = useQuery<
    ArtistSubscriptionPlanQuery,
    ArtistSubscriptionPlanQueryVariables
  >(ArtistSubscriptionPlan)

  const [createAcceptedItem] = useMutation<
    CreateAcceptedItemMutation,
    CreateAcceptedItemMutationVariables
  >(CREATE_ACCEPTED_ITEM, {
    onCompleted() {
      router.goBack(routerPages.ConsoleItems)
    },
  })

  const [createResource] = useMutation<
    CreateResourceMutation,
    CreateResourceMutationVariables
  >(CreateResource)

  const plans = useMemo(() => {
    if (data?.artist?.subscription?.products?.length) {
      return flatten(
        data?.artist?.subscription?.products.map(product => {
          const monthPlan = product.plans.find(
            plan => plan.interval === MembershipInterval.Month
          )
          if (monthPlan) {
            return [{ ...monthPlan, title: product.title }]
          }
          return product.plans.map(plan => ({ ...plan, title: product.title }))
        })
      )
    }
    if (data?.artist?.subscription?.plans?.length) {
      return data.artist.subscription.plans
    }
    return []
  }, [data?.artist?.subscription?.plans, data?.artist?.subscription?.products])

  return {
    state: {
      artistId: user?.uid ?? '',
      resourceType: router.query.resourceType,
      plans,
      progress,
    },
    actions: {
      /**
       * ファイルアップロード時に呼ばれる処理
       * @param {ItemType} resourceType リソース型
       * @param {boolean} hasCreateResource artist.resourceドキュメントを作るかどうか、resourceまたはwallpaperResourceをcreateする際のみtrueにする
       */
      async onDrop(resourceType, hasCreateResource) {
        const file = await getResource(resourceType).catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(
          file,
          [ItemType.Audio, ItemType.Video, ItemType.Magazine].includes(
            resourceType
          ),
          true
        )
        const uuid = createUuid()

        if (hasCreateResource) {
          const { data } = await createResource({
            variables: {
              name: uuid,
              filePath: tmpPath,
            },
          })
          return {
            id: data?.createResource.id ?? uuid,
            resourceName: data?.createResource.id ?? uuid,
            imageUrl: data?.createResource?.uri,
            imageUploadPath: tmpPath,
          }
        }
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onSubmit(values) {
        if (values.type === ItemType.Image) {
          return
        }
        // 個人情報必須の区分け
        let personalInformationRequirements:
          | PersonalInformationType[]
          | undefined = undefined
        if (values.type === ItemType.Ticket) {
          switch (values.personalInformationRequirements) {
            case personalInformationRequirementsValue.nameAndTel:
              personalInformationRequirements = [
                PersonalInformationType.FullName,
                PersonalInformationType.PhoneNumber,
              ]
              break
            case personalInformationRequirementsValue.nameAndTelAndAddress:
              personalInformationRequirements = [
                PersonalInformationType.FullName,
                PersonalInformationType.PhoneNumber,
                PersonalInformationType.Address,
              ]
              break
            default:
              personalInformationRequirements = []
              break
          }
        }

        const { metadata, resaleEnabled } = values
        if (values.type === ItemType.Ticket && !resaleEnabled && metadata) {
          set(metadata, 'ticket.resalePeriod', undefined)
        }
        if (values.type === ItemType.Ticket && metadata) {
          set(
            metadata,
            'ticket.throughTicket',
            getThroughTicket(
              metadata.ticket?.isThroughTicket
                ? metadata.ticket?.throughTicket
                : null
            )
          )
          if (metadata.ticket?.isThroughTicket) {
            set(metadata, 'ticket.startDateOfUse', null)
            set(metadata, 'ticket.endDateOfUse', null)
          }
        }
        if (
          values.type === ItemType.Ticket &&
          metadata?.ticket?.startAssignedNumber
        ) {
          set(
            metadata,
            'ticket.startAssignedNumber',
            +metadata.ticket.startAssignedNumber
          )
        }
        await createAcceptedItem({
          variables: {
            inputs: {
              artistId: user?.uid ?? '',
              isDownloadable: values.isDownloadable,
              // 壁紙・アバターのとき、resourceIdをnullとして扱う
              resourceId: ['wallpaper', 'avatar'].includes(values.type)
                ? null
                : values.resourceId,
              ...removeItemInput[values.type],
              personalInformationRequirements,
              ...pick(values, [
                'type',
                'price',
                'title',
                'thumbnail',
                'isPremium',
                'resaleEnabled',
                'issuedNumber',
                'description',
                'thumbnailBack',
                'thumbnailType',
                'avatarResources',
                'wallpaperResources',
                'purchaseLimitByUser',
                'purchaseLimitPerOrder',
                'hideSalesNumber',
                'purchesablePlanIds',
              ]),
              metadata,
              salesStartedAt: values.salesStartedAt || new Date(),
              salesEndedAt: values.salesEndedAt || null,
              salesStatus: values.salesStatus
                ? SalesStatus.Active
                : SalesStatus.Stopped,
              paymentType: values.price > 0 ? values.paymentType : null,
              skuSettings: getSkuSettings(
                values.hasSku,
                values.issuedNumber,
                values.skuSettings
              ),
              images: values.images?.filter(img => !!img.tmpPath),
              copyrights: [],
            },
          },
        })
      },
    },
  }
})

const removeItemInput: Record<ItemType, Partial<CreateItemInput>> = {
  [ItemType.Wallpaper]: {
    avatarResources: undefined,
    metadata: undefined,
  },
  [ItemType.Video]: {
    wallpaperResources: undefined,
    avatarResources: undefined,
    isDownloadable: undefined,
    metadata: undefined,
  },
  [ItemType.Ticket]: {
    resourceId: undefined,
    isDownloadable: undefined,
    wallpaperResources: undefined,
    avatarResources: undefined,
  },
  [ItemType.Audio]: {
    isDownloadable: undefined,
    wallpaperResources: undefined,
    avatarResources: undefined,
    metadata: undefined,
  },
  [ItemType.Avatar]: {
    isDownloadable: undefined,
    wallpaperResources: undefined,
    metadata: undefined,
  },
  [ItemType.Magazine]: {
    isDownloadable: undefined,
    wallpaperResources: undefined,
    avatarResources: undefined,
    metadata: undefined,
  },
  [ItemType.Merch]: {
    isDownloadable: undefined,
    resourceId: undefined,
    wallpaperResources: undefined,
    avatarResources: undefined,
  },
  [ItemType.Image]: {},
}

export function getSkuSettings(
  hasSku: boolean,
  issuedNumber?: Maybe<number>,
  value?: Maybe<SkuSettingsInput>
): SkuSettingsInput | undefined {
  if (!hasSku || !value?.units?.length) return undefined
  return {
    keyName: value?.keyName,
    units: value.units.map((unit, index) => ({
      id: unit?.id,
      unitName: unit.unitName,
      stock: unit.stock || (issuedNumber ? 0 : null),
      thumbnail: unit.thumbnail,
      price: unit.price ?? null,
      order: index + 1,
    })),
  }
}

export function getThroughTicket(
  throughTicket?: Maybe<TicketItemMetadataThroughTicketInput[]>
) {
  return (
    throughTicket?.map(({ name, startDateOfUse, endDateOfUse }) => ({
      name,
      startDateOfUse,
      endDateOfUse,
    })) ?? null
  )
}

const CREATE_ACCEPTED_ITEM = gql`
  mutation CreateAcceptedItem($inputs: CreateItemInput) {
    createAcceptedItem(inputs: $inputs) {
      ...ItemSelectBox
    }
  }
  ${ItemSelectBoxFragmentDoc}
`
